/**
 * 認証ポリシー（設定 > 認証 > 認証ポリシー）の Contract。**publish 前のローカル定義**。
 *
 * dresscode-backend #8514 の
 * `shared-kernel/api/settings/usecase/authentication-policy.api-contract.ts` を写したもの。
 * publish 後はこのファイルを削除し `@standandforce/api-contract` からの import に差し替える
 * （差し替えを import 変更だけで済ませるため、BE のファイル分割・スキーマ名をそのまま保つ）。
 *
 * JSON の意味論（catch-all 必須・DENY の obligation 禁止等）の検証はドメイン層が唯一の正で、
 * create / update / validate のすべてが同じロジックを通る。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { pageSizeAllowingAllSchema, paginatedSchema, paginationQuerySchema } from './pagination';
export const AuthenticationPolicyStatusSchema = z.enum(['DRAFT', 'REPORT_ONLY', 'ENFORCED']);
export const AuthenticationPolicyConditionJoinSchema = z.enum(['AND', 'OR']);
const PersonalEmailLoginModeEnum = z.enum(['DISALLOWED', 'UNTIL_WORK_EMAIL_SET', 'ALWAYS_ALLOWED']);

/** Ory の Authenticator Assurance Level（aal2 = MFA 済み）。 */
const AalEnum = z.enum(['aal1', 'aal2']);
const LoginIdentifierEnum = z.enum(['work', 'personal']);

/** 許可ログイン方式・USED_LOGIN_METHOD 条件で共有する方式選択。 */
export const AuthenticationPolicyLoginMethodSelectionSchema = z.object({
  includesPassword: z.boolean(),
  /** federated（SSO）方式として許可する SSO 接続 ID */
  ssoConnectionIds: z.array(z.string().min(1))
});
const leafConditionOptions = [z.object({
  kind: z.literal('NETWORK_IN'),
  /** it-network 台帳の Network ID */
  networkIds: z.array(z.string().min(1)).min(1)
}), z.object({
  kind: z.literal('DEVICE_IN_GROUPS'),
  /** it-device 台帳の DeviceGroup ID */
  deviceGroupIds: z.array(z.string().min(1)).min(1)
}), z.object({
  kind: z.literal('SESSION_IS_FEDERATED')
}), z.object({
  kind: z.literal('SESSION_AAL_AT_LEAST'),
  level: AalEnum
}), z.object({
  kind: z.literal('USED_LOGIN_METHOD'),
  includesPassword: z.boolean(),
  ssoConnectionIds: z.array(z.string().min(1))
}), z.object({
  kind: z.literal('LOGIN_IDENTIFIER'),
  identifier: LoginIdentifierEnum
})] as const;
export const AuthenticationPolicyLeafConditionSchema = z.discriminatedUnion('kind', [...leafConditionOptions]);

/** ルール直下の条件ノード（葉 or 1 段グループ。グループは葉のみを持つ）。 */
export const AuthenticationPolicyConditionNodeSchema = z.discriminatedUnion('kind', [...leafConditionOptions, z.object({
  kind: z.literal('GROUP'),
  join: AuthenticationPolicyConditionJoinSchema,
  conditions: z.array(AuthenticationPolicyLeafConditionSchema).min(1)
})]);
export const AuthenticationRequirementElementSchema = z.discriminatedUnion('kind', [z.object({
  kind: z.literal('FEDERATED_SESSION')
}), z.object({
  kind: z.literal('AAL_AT_LEAST'),
  level: AalEnum
})]);

/**
 * ルールの outcome。各 obligation の `null` = 制限なし。
 * DENY は obligation を持てない（enforceFrom のみ許容 = 予告された遮断）。
 */
export const AuthenticationPolicyOutcomeSchema = z.object({
  decision: z.enum(['ALLOW', 'DENY']),
  /** 認証強度（anyOf: いずれか 1 つ満たせば充足） */
  authenticationRequirement: z.array(AuthenticationRequirementElementSchema).nullable(),
  allowedLoginMethods: AuthenticationPolicyLoginMethodSelectionSchema.nullable(),
  personalEmailMode: PersonalEmailLoginModeEnum.nullable(),
  /** 強制の発効日時（オフセット付き ISO8601）。これ以降のみこのルールが効く。 */
  enforceFrom: z.string().datetime({
    offset: true
  }).nullable()
});
export const AuthenticationPolicyRuleInputSchema = z.object({
  /** 表示名（UI のルール見出し）。null = 無題 */
  name: z.string().nullable(),
  conditionJoin: AuthenticationPolicyConditionJoinSchema,
  /** 空 = 無条件（catch-all）。最終ルールのみ空にできる */
  conditions: z.array(AuthenticationPolicyConditionNodeSchema),
  outcome: AuthenticationPolicyOutcomeSchema
});
export const AuthenticationPolicyRuleViewSchema = AuthenticationPolicyRuleInputSchema.extend({
  id: z.string()
});

/** create / update / validate 共通の下書き形状。priority は作成時に自動採番される。 */
export const AuthenticationPolicyDraftSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable(),
  status: AuthenticationPolicyStatusSchema,
  /** ruleOrder は配列順。最終要素は条件なしの catch-all であること */
  rules: z.array(AuthenticationPolicyRuleInputSchema).min(1)
});
export const AuthenticationPolicySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number().int(),
  isDefault: z.boolean(),
  status: AuthenticationPolicyStatusSchema,
  ruleCount: z.number().int(),
  /** このポリシーを参照するアクセスルール数（UI の「適用: N 件」） */
  attachmentCount: z.number().int(),
  updatedAt: z.string().datetime()
});
export const AuthenticationPolicyDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priority: z.number().int(),
  isDefault: z.boolean(),
  status: AuthenticationPolicyStatusSchema,
  rules: z.array(AuthenticationPolicyRuleViewSchema),
  attachmentCount: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
const DisplayMessagesSchema = z.object({
  en: z.string(),
  ja: z.string(),
  id: z.string(),
  vn: z.string(),
  th: z.string(),
  zhCN: z.string()
});
export const AuthenticationPolicyValidationIssueSchema = z.object({
  /** 指摘位置（例: `rules[1].conditions[0]`）。ポリシー全体への指摘は null */
  path: z.string().nullable(),
  /** エラー種別の安定コード（例: `MissingCatchAllRule`） */
  code: z.string(),
  messages: DisplayMessagesSchema
});
export const AuthenticationPolicyValidateResultSchema = z.object({
  valid: z.boolean(),
  issues: z.array(AuthenticationPolicyValidationIssueSchema)
});

/** 更新対象の既存ポリシーを指定すると、その isDefault / 名前重複を含めて検証する。 */
export const AuthenticationPolicyValidateInputSchema = AuthenticationPolicyDraftSchema.extend({
  id: z.string().nullable().optional()
});

/** 組織あたりの認証ポリシー数上限（BE と同値。reorder の orderedIds.max に使う）。 */
export const MAX_AUTHENTICATION_POLICIES = 200;
const listOrderFields = z.enum(['priority', 'name', 'updatedAt']);
const AuthenticationPolicyListQuerySchema = paginationQuerySchema(listOrderFields).extend({
  pageSize: pageSizeAllowingAllSchema()
});

/**
 * 並び替え入力。非デフォルトポリシー全件の ID を新しい評価順（先頭が最優先）で列挙する。
 * 部分並び替えは受け付けない。
 */
export const AuthenticationPolicyReorderInputSchema = z.object({
  orderedIds: z.array(z.string().min(1)).min(1).max(MAX_AUTHENTICATION_POLICIES)
});
export const AuthenticationPolicyReorderResultSchema = z.object({
  /** 並び替え後の全ポリシー（評価順。デフォルトは常に最後） */
  items: z.array(AuthenticationPolicySummarySchema)
});
export const authenticationPolicyContract = oc.router({
  list: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policies'
  }).input(AuthenticationPolicyListQuerySchema).output(paginatedSchema(AuthenticationPolicySummarySchema)),
  get: oc.route({
    method: 'GET',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(z.object({
    id: z.string()
  })).output(AuthenticationPolicyDetailSchema),
  create: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies'
  }).input(AuthenticationPolicyDraftSchema).output(AuthenticationPolicyDetailSchema),
  update: oc.route({
    method: 'PUT',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(AuthenticationPolicyDraftSchema.extend({
    id: z.string()
  })).output(AuthenticationPolicyDetailSchema),
  delete: oc.route({
    method: 'DELETE',
    path: '/v2/settings/authentication-policies/{id}'
  }).input(z.object({
    id: z.string()
  })).output(z.object({
    id: z.string()
  })),
  /**
   * 保存せずに下書きを検証する。create / update と同じ検証ロジック
   * （構造の不変条件 + 参照 SSO 接続の実在性）を通し、指摘を全件返す。
   */
  validate: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/validate'
  }).input(AuthenticationPolicyValidateInputSchema).output(AuthenticationPolicyValidateResultSchema),
  /**
   * 非デフォルトポリシーの評価順（priority）を一括で並び替える。
   * デフォルトポリシーは常に最後に評価されるため対象外。
   */
  reorder: oc.route({
    method: 'POST',
    path: '/v2/settings/authentication-policies/reorder'
  }).input(AuthenticationPolicyReorderInputSchema).output(AuthenticationPolicyReorderResultSchema)
});
export type AuthenticationPolicyStatus = z.infer<typeof AuthenticationPolicyStatusSchema>;
export type AuthenticationPolicyConditionJoin = z.infer<typeof AuthenticationPolicyConditionJoinSchema>;
export type AuthenticatorAssuranceLevel = z.infer<typeof AalEnum>;
export type LoginIdentifierKind = z.infer<typeof LoginIdentifierEnum>;
export type PersonalEmailLoginMode = z.infer<typeof PersonalEmailLoginModeEnum>;
export type AuthenticationPolicyLoginMethodSelection = z.infer<typeof AuthenticationPolicyLoginMethodSelectionSchema>;
export type AuthenticationPolicyLeafCondition = z.infer<typeof AuthenticationPolicyLeafConditionSchema>;
export type AuthenticationPolicyConditionKind = AuthenticationPolicyLeafCondition['kind'];
export type AuthenticationPolicyConditionNode = z.infer<typeof AuthenticationPolicyConditionNodeSchema>;
export type AuthenticationRequirementElement = z.infer<typeof AuthenticationRequirementElementSchema>;
export type AuthenticationPolicyOutcome = z.infer<typeof AuthenticationPolicyOutcomeSchema>;
export type AuthenticationPolicyRuleInput = z.infer<typeof AuthenticationPolicyRuleInputSchema>;
export type AuthenticationPolicyRuleView = z.infer<typeof AuthenticationPolicyRuleViewSchema>;
export type AuthenticationPolicyDraftInput = z.infer<typeof AuthenticationPolicyDraftSchema>;
export type AuthenticationPolicySummary = z.infer<typeof AuthenticationPolicySummarySchema>;
export type AuthenticationPolicyDetail = z.infer<typeof AuthenticationPolicyDetailSchema>;
export type AuthenticationPolicyValidationIssue = z.infer<typeof AuthenticationPolicyValidationIssueSchema>;
export type AuthenticationPolicyValidateResult = z.infer<typeof AuthenticationPolicyValidateResultSchema>;
export type AuthenticationPolicyValidateInput = z.infer<typeof AuthenticationPolicyValidateInputSchema>;
export type AuthenticationPolicyReorderInput = z.infer<typeof AuthenticationPolicyReorderInputSchema>;
export type AuthenticationPolicyReorderResult = z.infer<typeof AuthenticationPolicyReorderResultSchema>;